import get from 'lodash/get';
import { Spin } from 'platform-components';
// @ts-ignore
import { Redirect } from 'platform-components/src/components/router/router';
import { getIsParent } from 'platform-components/src/components/user/selectors';
import { withRouterPath, InjectedProps as WithRouterPathHOC } from 'platform-components/hocs';
import { getExactPathMatch } from 'platform-components/utils';
import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet';
import { WrappedComponentProps, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';

import ChangesNotification from 'common.components/changes-notification/changes-notification';

import { TeacherHeader } from 'common.components/TeacherHeader/TeacherHeader';
import { getFirstActiveTeamId } from 'common.components/user/selectors';
import { actions as coursesActions } from 'store/courses/actions';
import { getSubjects, getShouldLoadCourses } from 'store/courses/selectors';
import { CourseSubject } from 'store/courses/typings';
import { getInitialSubject } from 'store/subject-config/selectors';
import { courseSubscriptionsActions } from 'store/subscriptions/actions';
import { getCoursesBySubjectUtil, getCurrentYearCourseUtil } from 'utils/get-course';
// @ts-ignore
import { YM_LOCATION } from 'utils/ym-constants';

import { ReduxTeacherStore, RootAction } from '../../typings';
import CourseView from '../course-view/course-view';
import EmptyState, { EMPTY_STATE_ACTION } from '../empty-state/empty-state';

import { getShouldShowPageSpinner } from './selectors';

interface StoreProps {
    initialSubject: string;
    firstActiveTeamId: number | null;
    shouldLoadCourses: boolean;
    shouldShowSpinner: boolean;
    subjects: Array<CourseSubject>;
    isParent: boolean;
}

interface DispatchProps {
    getCoursesByTeamRequest: typeof coursesActions.getCoursesByTeamRequest;
    getCoursesByTeamSuccess: typeof coursesActions.getCoursesByTeamSuccess;
    getSubscriptionsByCourse: typeof courseSubscriptionsActions.getSubscriptionsByCourse;
}

interface OwnProps extends WithRouterPathHOC, WrappedComponentProps {}

interface Props extends OwnProps, StoreProps, DispatchProps {}

export class CourseViewLoader extends React.Component<Props> {
    static contextTypes = {
        // eslint-disable-next-line react/forbid-prop-types
        data: PropTypes.object,
        // eslint-disable-next-line react/forbid-prop-types
        staticUrls: PropTypes.object,
        initialRender: PropTypes.bool,
    };

    constructor(props: Props, context: unknown) {
        super(props, context);

        const {
            getCoursesByTeamRequest,
            getCoursesByTeamSuccess,
            shouldLoadCourses,
            getSubscriptionsByCourse,
            match: { params },
            isParent,
        } = props;
        const classId = params?.classId;
        const subject = params?.subject;
        // @ts-ignore
        const { initialRender, data } = context;

        if (initialRender && data.getCourses) {
            getCoursesByTeamSuccess({ teamId: Number(classId), courses: data.getCourses });
            if (isParent && subject) {
                const courses = getCoursesBySubjectUtil(data.getCourses, subject);
                const courseId = get(getCurrentYearCourseUtil(courses), 'id', null);
                if (courseId) {
                    getSubscriptionsByCourse({ courseId });
                }
            }
        } else if (shouldLoadCourses) {
            getCoursesByTeamRequest({ teamId: Number(classId) });
        }
    }

    componentDidUpdate() {
        const {
            initialSubject,
            getCoursesByTeamRequest,
            match: { params },
            shouldLoadCourses,
            subjects,
            getRouterPath,
            history,
        } = this.props;
        const { classId, subject } = params || {};
        const { staticUrls } = this.context;

        if (shouldLoadCourses) {
            getCoursesByTeamRequest({ teamId: Number(classId) });
        }

        // выход из empty-state предметников
        if (!subject && subjects?.length > 0) {
            const { subject: availableSubject } = subjects.find((s) => s.subject === initialSubject) || subjects[0];

            history.push(
                getRouterPath(staticUrls.labSubjectLessonsComplete, { subject: availableSubject }) +
                    history.location.search
            );
        }
    }
    context!: React.ContextType<React.Context<{ staticUrls: Record<string, string> }>>;

    renderContent = () => {
        const {
            initialSubject,
            shouldShowSpinner,
            subjects,
            match: { params },
            location,
            getRouterPath,
        } = this.props;
        const { subject } = params || {};
        const { staticUrls } = this.context;

        if (shouldShowSpinner) {
            return <Spin center mix="course-view__spin" />;
        }

        if (subjects.length === 0) {
            return (
                <div className="course-view">
                    <EmptyState
                        action={EMPTY_STATE_ACTION.ADD_SUBJECT}
                        locationForMetrika={YM_LOCATION.MY_LESSONS}
                        textId="courseView.emptyState.noSubject"
                    />
                </div>
            );
        }

        if (!subjects.find((courseSubject) => courseSubject.subject === subject)) {
            const correctSubject = subjects.find((courseSubject) => courseSubject.subject === initialSubject)
                ? initialSubject
                : subjects[0].subject;
            const { path = staticUrls.home } = getExactPathMatch(staticUrls, location.pathname) || {};

            return <Redirect to={`${getRouterPath(path, { subject: correctSubject })}${location.search}`} />;
        }

        return <CourseView />;
    };

    render() {
        const { staticUrls } = this.context;
        const {
            getRouterPath,
            location,
            match: { params },
            firstActiveTeamId,
            intl,
        } = this.props;

        const { classId } = params || {};

        // если classId – архивный, то редиректим в сохраненный или первый открытый класс
        if (Number(classId) !== firstActiveTeamId) {
            let link = getRouterPath(staticUrls.labLessons);

            if (firstActiveTeamId) {
                link = getRouterPath(staticUrls.labClassLessons, { classId: firstActiveTeamId });
            }

            return <Redirect to={`${link}${location.search}`} />;
        }

        return (
            <>
                <Helmet title={intl.formatMessage({ id: 'myLessonsPage.title' })} />
                <TeacherHeader className="course-view__nav" />
                <ChangesNotification />
                {this.renderContent()}
            </>
        );
    }
}

const mapStateToProps = (storeState: ReduxTeacherStore, ownProps: OwnProps): StoreProps => {
    const {
        match: { params },
    } = ownProps;
    const classId = Number(params?.classId);

    return {
        initialSubject: getInitialSubject(storeState),
        shouldLoadCourses: getShouldLoadCourses(storeState, { classId }),
        firstActiveTeamId: getFirstActiveTeamId(storeState, { classId }),
        shouldShowSpinner: getShouldShowPageSpinner(storeState, { classId }),
        subjects: getSubjects(storeState, { classId }),
        isParent: getIsParent(storeState),
    };
};

const mapDispatchToProps = (dispatch: Dispatch<RootAction>): DispatchProps => {
    const { getCoursesByTeamRequest, getCoursesByTeamSuccess } = coursesActions;
    const { getSubscriptionsByCourse } = courseSubscriptionsActions;

    return bindActionCreators(
        {
            getCoursesByTeamRequest,
            getCoursesByTeamSuccess,
            getSubscriptionsByCourse,
        },
        dispatch
    );
};

export default injectIntl(withRouterPath(connect(mapStateToProps, mapDispatchToProps)(CourseViewLoader)));
